import React from 'react';

export type TextInputPropsType = {
	disabled?: boolean;
	value: string;
	onChange: (valueOrEvent: string | { target: { value: string } }) => void;
	onFocus: () => void;
	onBlur: () => void;
	label: string;
	key: string;
	error: boolean;
	errorMessage?: string;
	helperText?: string;
};

const valueExceedsLimit = (value?: string | number | null, limit?: number): boolean => {
	return limit && value
		? typeof value === 'string'
			? value.length > limit
			: typeof value === 'number'
			? value.toString().length > limit
			: false
		: false;
};

type StateValueFallbackType = '' | null | undefined;

const useTextFieldContoller = <InputType extends number | string>({
	defaultValue,
	inputLabel,
	stateKey,
	type = 'STRING',
	limit,
	validationFunction,
	disabledHelperText,
	disabled,
	stateValueFallback = undefined,
	onChange,
	finishedInitializing,
}: {
	defaultValue?: InputType;
	inputLabel: string;
	stateKey: string;
	type?: 'STRING' | 'NUMBER';
	limit?: number;
	validationFunction?: (value: string) => string | undefined;
	disabledHelperText?: string;
	disabled?: boolean;
	stateValueFallback?: StateValueFallbackType;
	onChange?: (newStateValue: InputType | StateValueFallbackType) => void;
	finishedInitializing?: boolean;
}): {
	textInputProps: TextInputPropsType;
	newState: { [key: string]: InputType | StateValueFallbackType };
	newStateValue: InputType | StateValueFallbackType;
	changed: boolean;
	exceeded: boolean;
	selected: boolean;
	error: boolean;
	validate?: () => boolean;
} => {
	const [stateValue, setStateValue] = React.useState<InputType | StateValueFallbackType>(
		defaultValue || stateValueFallback
	);
	const [textFieldValue, setTextFieldValue] = React.useState<string>(
		(defaultValue && defaultValue.toString()) || ''
	);
	const [changed, setChanged] = React.useState(false);
	const [exceeded, setExceeded] = React.useState(valueExceedsLimit(textFieldValue, limit));
	const [errorMessage, setErrorMessage] = React.useState<string | undefined>();

	const getHelperText = React.useCallback(() => {
		if (errorMessage) {
			return errorMessage;
		} else if (limit) {
			return `${textFieldValue.length}/${limit}`;
		} else if (disabled && disabledHelperText) {
			return disabledHelperText;
		} else {
			return undefined;
		}
	}, [disabled, disabledHelperText, errorMessage, limit, textFieldValue.length]);

	const [helperText, setHelperText] = React.useState<string | undefined>(getHelperText());
	const [selected, setSelected] = React.useState(false);

	const handleValidate = React.useCallback(() => {
		const newErrorMessage = validationFunction ? validationFunction(textFieldValue) : undefined;
		setErrorMessage(newErrorMessage);
		return Boolean(newErrorMessage);
	}, [textFieldValue, validationFunction]);

	const handleInputChange = React.useCallback(
		(eventOrValue) => {
			setErrorMessage(undefined);
			const newValue = eventOrValue?.target?.value || eventOrValue;
			const newTextFieldValue: string | '' = typeof newValue === 'string' ? newValue : '';
			const newStateValue =
				newTextFieldValue !== ''
					? type === 'NUMBER'
						? Number(newTextFieldValue)
						: newTextFieldValue
					: stateValueFallback;
			const stateValueChanged =
				!newStateValue && !defaultValue ? false : newStateValue !== defaultValue;
			setTextFieldValue(newTextFieldValue);
			setExceeded(valueExceedsLimit(newTextFieldValue, limit));
			setHelperText(getHelperText());
			setStateValue(newStateValue as InputType | StateValueFallbackType);
			setChanged(stateValueChanged);
			onChange?.(newStateValue as InputType | StateValueFallbackType);
		},
		[type, stateValueFallback, defaultValue, limit, getHelperText, onChange]
	);

	const handleFocus = React.useCallback(() => {
		setSelected(true);
	}, []);

	const handleBlur = React.useCallback(() => {
		setSelected(false);
	}, []);

	React.useEffect(() => {
		if (finishedInitializing) {
			setStateValue(defaultValue || stateValueFallback);
			setTextFieldValue((defaultValue && defaultValue.toString()) || '');
		}
	}, [defaultValue, finishedInitializing, stateValueFallback]);

	React.useEffect(() => {
		setHelperText(getHelperText());
	}, [disabled, getHelperText]);

	return React.useMemo(
		() => ({
			textInputProps: {
				value: textFieldValue,
				onChange: handleInputChange,
				onFocus: handleFocus,
				onBlur: handleBlur,
				label: inputLabel,
				key: stateKey,
				error: Boolean(errorMessage) || exceeded,
				helperText,
				disabled,
			},
			newState: { [stateKey]: stateValue },
			newStateValue: stateValue,
			changed,
			exceeded,
			selected,
			error: Boolean(errorMessage) || exceeded,
			validate: handleValidate,
		}),
		[
			textFieldValue,
			handleInputChange,
			handleFocus,
			handleBlur,
			inputLabel,
			stateKey,
			errorMessage,
			exceeded,
			helperText,
			disabled,
			stateValue,
			changed,
			selected,
			handleValidate,
		]
	);
};

export default useTextFieldContoller;
